import { sendLogin } from '~/composables/useGtmEvents'
import useAccount from '~/composables/auth/useAccount'

/**
 * Fires the GA4/GTM `login` event for social (OAuth) logins on the post-login
 * destination page instead of on the `/auth/{provider}/callback/` route.
 *
 * The callback route is excluded from GTM initialization (see
 * `app/middleware/3.gtm.global.ts`, KFT-3806) to avoid minting a phantom
 * "Direct" GA4 session, so a `login` push made there would be dropped. The
 * callback redirects to the destination with `social_auth_status=success` and
 * `social_auth_type=<provider>`; we detect that here and fire the event where
 * GTM is live. Meant to be called once, globally, from `app.vue`.
 */
export const useSocialLoginTracking = () => {
  if (import.meta.server) return

  const route = useRoute()
  const router = useRouter()
  const { user, fetchProfile } = useAccount()

  let hasFired = false

  const handleSocialLoginReturn = async (status: unknown) => {
    if (status !== 'success' || hasFired) return
    hasFired = true

    const method = (route.query.social_auth_type as string) || 'unknown'

    // Ensure the profile is loaded (fetchProfile dedupes in-flight requests) and
    // that gtm.enable() from the route middleware has run before we push, so the
    // event isn't gated out by useKftGtm's `gtm.enabled()` check.
    if (!user.value) {
      try {
        await fetchProfile()
      } catch {
        // fall through with a null user id, matching the prior callback behavior
      }
    }

    sendLogin(method, user.value?.data?.id || null)

    // Drop the one-shot social-auth params so a reload doesn't re-fire `login`,
    // preserving any other query params (e.g. `step`).
    const query = { ...route.query }
    delete query.social_auth_status
    delete query.social_auth_type
    await router.replace({ query })
  }

  watch(
    () => route.query.social_auth_status,
    (status) => {
      handleSocialLoginReturn(status)
    },
    { immediate: true }
  )
}
